/**
 * Open-a-print-form bus. `openPrintWindow()` is called from outside the React
 * tree (list row actions, form command bars), so the request travels through a
 * module-level listener set rather than context.
 *
 * Deliberately free of React and the router: this module is pulled in
 * transitively by the record form (for `hasActivePrintWindow`), and a router
 * import here breaks every test suite that renders a form without mocking
 * react-router-dom. The component that turns a request into a workspace tab
 * lives in the app layer (`app/workspace/PrintTabOpener`).
 */
import type { PrintWindowDescriptor } from './openPrintWindow'
import { hasOpenPrintSessions } from './printSessions'

type OpenListener = (descriptor: PrintWindowDescriptor) => void

const listeners = new Set<OpenListener>()

/** True while at least one print tab is open (gates the record form's ⌘S / ⌘⏎). */
export function hasActivePrintWindow(): boolean {
  return hasOpenPrintSessions()
}

/** False when nothing is listening — the caller then falls back to a browser tab. */
export function emitOpenPrintWindow(descriptor: PrintWindowDescriptor): boolean {
  if (listeners.size === 0) return false
  listeners.forEach((fn) => fn(descriptor))
  return true
}

export function subscribeOpenPrintWindow(fn: OpenListener): () => void {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}
